import { isGiftContactComplete } from "@/lib/gift-phone";
import { metaLazy } from "@/lib/metaLazy";
import { CartMarketRecoveryNotice } from "@/components/cart/CartMarketRecoveryNotice";
import { checkoutInCurrentTab } from "@/lib/checkout-navigation";
import * as React from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronDown,
  CreditCard,
  Gift,
  Heart,
  Loader2,
  MapPin,
  Minus,
  Pencil,
  Phone,
  Plus,
  ShoppingBag,
  Sparkles,
  Trash2,
  UserPlus,
  X,
  ZoomIn,
} from "lucide-react";
import { SiteShell } from "@/components/SiteShell";
import { useCartStore, CartItem, GiftDetails } from "@/stores/cartStore";
import { useLocale } from "@/contexts/LocaleContext";
import { Button } from "@/components/ui/button";
import { useWishlist } from "@/hooks/useWishlist";
import { toast } from "@/lib/toast";

import { CartRecommendations } from "@/components/CartRecommendations";
import { CartGiftAttachAlert } from "@/components/CartGiftAttachAlert";
import { GiftFlow } from "@/components/GiftFlow";
import { GiftPicker } from "@/components/GiftPicker";
import { GiftProgressBar } from "@/components/GiftProgressBar";
import { GiftReason } from "@/components/GiftReason";
import { MARKETS } from "@/lib/markets";
import { shopifyImg, shopifySrcSet } from "@/lib/shopify";
import { cn } from "@/lib/utils";
import { getCartItemPresentation } from "@/lib/cart-item-presentation";
import { cartSubtotal, formatContextMoney, multiplyContextMoney, productDeliveryPolicy } from "@/lib/storefront-cart-context";
import { CartFreeShippingNotice } from "@/components/CartFreeShippingNotice";
import { CartImagePopup, type CartPopupImage } from "@/components/CartImagePopup";

const WHATSAPP_NUMBER = "971508877701";
const PHONE_NUMBER = "+971508877701";

export const Route = createFileRoute("/cart")({
  component: CartPage,
  head: () => ({
    meta: [
      { title: "My Bag — Lecmo Perfume" },
      { property: "og:title", content: "My Bag — Lecmo Perfume" },
      { property: "og:description", content: "Review your fragrances and confirmed contextual prices before checkout." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      {
        name: "description",
        content: "Review your selected fragrances and proceed to a secure checkout.",
      },
    ],
  }),
});

function CartPage() {
  const navigate = useNavigate();
  const { t, formatPrice, dir, locale } = useLocale();
  const {
    items,
    isLoading,
    isSyncing,
    updateQuantity,
    removeItem,
    prepareCheckout,
    syncCart,
    cartGift,
    clearCartGift,
    contextualSubtotal,
    contextualMoneyItems,
    shippingQuote,
    cartMarketUncertain,
    cartCountry,
  } = useCartStore();

  const [giftFlowOpen, setGiftFlowOpen] = React.useState(false);
  const [summaryOpen, setSummaryOpen] = React.useState(true);

  // نافذة معاينة صور منتج واحد (popup مربّع)
  const [popupImages, setPopupImages] = React.useState<CartPopupImage[] | null>(null);
  const [popupTitle, setPopupTitle] = React.useState<string | null>(null);
  const [popupIndex, setPopupIndex] = React.useState(0);

  // معاينة صور منتج واحد فقط من السلّة
  const openProductPopup = React.useCallback((item: CartItem) => {
    const imgs: CartPopupImage[] = item.product.node.images.edges
      .map((e) => e?.node)
      .filter((n): n is NonNullable<typeof n> => !!n?.url)
      .map((n) => ({ url: n.url, alt: n.altText ?? item.product.node.title }));
    if (imgs.length === 0) return;
    setPopupImages(imgs);
    setPopupTitle(item.product.node.title);
    setPopupIndex(0);
  }, []);

  // ننتظر hydration من localStorage قبل اتخاذ قرار «السلة فارغة».
  // في بعض بيئات المعاينة على الموبايل قد لا يصل onFinishHydration دائماً،
  // لذا نُطلِق rehydrate يدوياً مع fallback آمن كي لا تبقى الصفحة skeleton إلى الأبد.
  const [hasHydrated, setHasHydrated] = React.useState(false);

  React.useEffect(() => {
    const persist = useCartStore.persist;
    if (!persist) {
      setHasHydrated(true);
      return;
    }
    let cancelled = false;
    let fallbackTimer: number | null = null;

    const finishHydration = () => {
      if (fallbackTimer) {
        window.clearTimeout(fallbackTimer);
        fallbackTimer = null;
      }
      if (!cancelled) setHasHydrated(true);
    };

    if (persist.hasHydrated()) {
      finishHydration();
      return;
    }

    const unsub = persist.onFinishHydration(finishHydration);
    fallbackTimer = window.setTimeout(finishHydration, 1200);

    void Promise.resolve(persist.rehydrate())
      .catch(() => undefined)
      .finally(finishHydration);

    return () => {
      cancelled = true;
      if (fallbackTimer) window.clearTimeout(fallbackTimer);
      unsub();
    };
  }, []);

  React.useEffect(() => {
    if (hasHydrated) syncCart();
  }, [syncCart, hasHydrated]);

  const totalItems = items.reduce((s, i) => s + i.quantity, 0);
  const confirmedMoney = items === contextualMoneyItems && !cartMarketUncertain;
  const subtotalMoney = cartMarketUncertain ? null : cartSubtotal(items, confirmedMoney ? contextualSubtotal : null);
  const subtotalLabel = subtotalMoney ? formatContextMoney(subtotalMoney, locale) : t("cart.money_pending");
  const shippingLabel = confirmedMoney && shippingQuote && shippingQuote.currencyCode === subtotalMoney?.currencyCode
    ? formatContextMoney(shippingQuote, locale) : t("cart.shipping_calculated");

  // اكتمال هدايا قبل السماح بـ checkout
  const hasIncompleteGift = !!cartGift && !isGiftContactComplete(cartGift);

  const onCheckout = async () => {
    if (hasIncompleteGift) return;
    await checkoutInCurrentTab(prepareCheckout, () => {
      const tracking: Promise<unknown>[] = [];
      // Meta: InitiateCheckout — GA4 begin_checkout مصدره Shopify حصرياً (منع تكرار)
      const checkoutItems = items.map((i) => ({
        id: i.product.node.id,
        title: i.product.node.title,
        price: i.price.amount,
        currency: i.price.currencyCode,
        quantity: i.quantity,
        variantTitle: i.variantTitle,
      }));
      tracking.push(metaLazy(({ trackInitiateCheckout }) =>
        trackInitiateCheckout(checkoutItems),
      ));
      tracking.push(import("@/lib/tiktokPixel").then(({ trackInitiateCheckout }) =>
        trackInitiateCheckout(
          items.map((i) => ({
            productId: i.product.node.id,
            variantId: i.variantId,
            title: i.product.node.title,
            price: i.price.amount,
            currency: i.price.currencyCode,
            quantity: i.quantity,
          })),
        ),
      ));
      tracking.push(import("@/lib/snapPixel").then(({ trackInitiateCheckout }) =>
            trackInitiateCheckout(
              items.map((i) => ({
                variantId: i.variantId,
                price: i.price.amount,
                currency: i.price.currencyCode,
                quantity: i.quantity,
              })),
            ),
      ));
      tracking.push(import("@/lib/googleAdsTag").then(({ trackBeginCheckout }) =>
        trackBeginCheckout(checkoutItems),
      ));
      tracking.push(import("@/lib/openaiAdsPixel").then(({ trackBeginCheckout }) =>
        trackBeginCheckout(checkoutItems),
      ));
      // 🗄️ تتبّعنا الأوّل-طرفيّ (customer_events)
      tracking.push(import("@/lib/activityTracker").then((m) => m.trackBeginCheckout()));

      tracking.push(import("@/lib/funnelClient").then(({ trackFunnelEvent }) => Promise.allSettled([
        trackFunnelEvent("cart_to_checkout", "begin_checkout", { source: "cart_page" }),
        trackFunnelEvent("checkout_to_purchase", "begin_checkout", { source: "cart_page" }),
      ])));

      return Promise.allSettled(tracking);
    }, () => toast.error(locale === "ar"
      ? "تعذّر فتح الدفع. حاول مرّة أخرى."
      : "Couldn’t open checkout. Please try again."));
  };

  // ─── انتظار hydration: skeleton بنفس تخطيط الصفحة النهائي لمنع layout shift ───
  if (!hasHydrated) {
    return (
      <SiteShell>
        <div dir={dir} className="bg-cart-page min-h-[70vh] text-cart-foreground">
          <div className="mx-auto max-w-7xl px-4 pb-32 md:px-6 md:pb-20">
            {/* عنوان وbreadcrumb skeleton */}
            <div className="mb-4 flex items-center justify-between gap-3 md:mb-6">
              <div className="animate-pulse rounded-none bg-primary/10 h-3 w-24" />
              <div className="animate-pulse rounded-none bg-primary/10 h-3 w-20" />
            </div>
            <div className="animate-pulse rounded-none bg-primary/10 mb-8 h-9 w-48 md:h-11 md:w-64" />

            {/* شبكة عمودين كالنهائي */}
            <div className="grid gap-8 lg:grid-cols-[1fr_380px] lg:gap-12">
              {/* قائمة العناصر */}
              <div className="space-y-5">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="flex gap-4 border border-border/40 bg-card/40 p-4 md:p-5"
                  >
                    <div className="animate-pulse rounded-none bg-primary/10 h-24 w-24 shrink-0 md:h-28 md:w-28" />
                    <div className="flex flex-1 flex-col gap-2.5">
                      <div className="animate-pulse rounded-none bg-primary/10 h-3 w-20" />
                      <div className="animate-pulse rounded-none bg-primary/10 h-5 w-3/4" />
                      <div className="animate-pulse rounded-none bg-primary/10 h-4 w-24" />
                      <div className="mt-auto flex items-center justify-between">
                        <div className="animate-pulse rounded-none bg-primary/10 h-8 w-24" />
                        <div className="animate-pulse rounded-none bg-primary/10 h-4 w-16" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* بطاقة الملخّص */}
              <aside className="h-fit space-y-4 border border-border/40 bg-card/40 p-5 md:p-6">
                <div className="animate-pulse rounded-none bg-primary/10 h-5 w-32" />
                <div className="space-y-3 pt-2">
                  <div className="flex justify-between">
                    <div className="animate-pulse rounded-none bg-primary/10 h-3 w-20" />
                    <div className="animate-pulse rounded-none bg-primary/10 h-3 w-16" />
                  </div>
                  <div className="flex justify-between">
                    <div className="animate-pulse rounded-none bg-primary/10 h-3 w-24" />
                    <div className="animate-pulse rounded-none bg-primary/10 h-3 w-14" />
                  </div>
                  <div className="flex justify-between border-t border-border/40 pt-3">
                    <div className="animate-pulse rounded-none bg-primary/10 h-4 w-16" />
                    <div className="animate-pulse rounded-none bg-primary/10 h-4 w-20" />
                  </div>
                </div>
                <div className="animate-pulse rounded-none bg-primary/10 h-12 w-full" />
                <div className="animate-pulse rounded-none bg-primary/10 h-3 w-2/3" />
              </aside>
            </div>
          </div>
        </div>
      </SiteShell>
    );
  }

  // ─── سلة فارغة ───
  if (items.length === 0) {
    return (
      <SiteShell>
        <div dir={dir} className="bg-cart-page min-h-[70vh] pb-20">
          <div className="mx-auto w-full max-w-3xl px-4 text-center sm:px-6">
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-none border border-border text-primary">
              <ShoppingBag className="h-7 w-7" />
            </div>
            <h1 className="t-h2 text-cart-foreground md:t-h1">
              {t("cart.empty")}
            </h1>
            <p className="mt-3 t-body-sm text-cart-muted">{t("cart.empty_sub")}</p>
            <Link
              to="/shop"
              className="mt-8 inline-flex items-center gap-1.5 t-eyebrow uppercase text-cart-muted transition-colors hover:text-primary"
            >
              {dir === "rtl" ? (
                <ArrowRight className="h-3.5 w-3.5" />
              ) : (
                <ArrowLeft className="h-3.5 w-3.5" />
              )}
              {t("cart.continue_shopping")}
            </Link>
          </div>
        </div>
      </SiteShell>
    );
  }

  return (
    <SiteShell>
      <div dir={dir} className="bg-cart-page text-cart-foreground">
        <div className="mx-auto max-w-7xl px-3 pb-[15rem] md:px-6 md:pb-20">
          {/* Breadcrumb / back — السهم يتبدّل تلقائياً حسب اتجاه القراءة */}
          <div className="mb-4 flex items-center justify-end gap-3 md:mb-6 md:justify-between">
            <Link
              to="/shop"
              className="hidden items-center gap-1.5 t-eyebrow uppercase text-cart-muted transition-colors hover:text-primary md:inline-flex"
            >
              {dir === "rtl" ? (
                <ArrowRight className="h-3.5 w-3.5" />
              ) : (
                <ArrowLeft className="h-3.5 w-3.5" />
              )}
              {t("cart.continue_shopping")}
            </Link>
          </div>

          {/* Header */}
          <div className="mb-6 md:mb-10">
            <h1 className="t-h2 md:t-h1">
              {t("cart.page_title")}{" "}
              <span className="text-cart-muted">({totalItems})</span>
            </h1>
          </div>

          {/* Layout: عمود واحد على الموبايل والتابلت · عمودان من اللابتوب فما فوق */}
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_380px] lg:gap-10 xl:gap-14">
            {/* ── LEFT: Items ── */}
            <div className="min-w-0">
              <div className="mb-4">
                <CartGiftAttachAlert />
              </div>
              <CartMarketRecoveryNotice />
              {/* الإهداء سمة للسلّة كاملة — مستلم واحد + بطاقة واحدة */}
              <div className="mb-4">
                {cartGift ? (
                  <CartLineGiftCard
                    gift={cartGift}
                    locale={locale}
                    t={t}
                    onEdit={() => setGiftFlowOpen(true)}
                    onRemove={async () => {
                      if (!(await clearCartGift())) toast.error(t("gift.remove_failed"), { duration: 8000 });
                    }}
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => setGiftFlowOpen(true)}
                    className="inline-flex w-full items-center justify-center gap-2 border border-primary/40 bg-primary/5 px-4 py-3 t-eyebrow uppercase text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
                  >
                    <Gift className="h-3.5 w-3.5" />
                    {t("cart.convert_to_gift")}
                  </button>
                )}
              </div>
              <ul className="divide-y divide-border bg-cart-card">
                {[...items]
                  // الهدايا المجّانيّة (sampleId) تنزل لأسفل القائمة لتبقى المنتجات المدفوعة في الأعلى.
                  .sort((a, b) => {
                    const aGift = a.sampleId ? 1 : 0;
                    const bGift = b.sampleId ? 1 : 0;
                    return aGift - bGift;
                  })
                  .map((item) => (
                    <li
                      key={item.lineId ?? item.variantId}
                      data-sample-line-id={item.sampleId ?? undefined}
                      className="px-4 py-5 md:p-6"
                    >
                      <CartLine
                        item={item}
                        locale={locale}
                        t={t}
                        formatPrice={formatPrice}
                        onUpdateQty={async (q) => {
                          if (item.lineId) await updateQuantity(item.lineId, q);
                        }}
                        onRemove={async () => {
                          if (item.lineId) await removeItem(item.lineId);
                        }}
                        onImageClick={() => openProductPopup(item)}
                      />
                    </li>
                  ))}
              </ul>

              {/* اختيار العيّنة المجّانيّة (حملة هدايا فعّالة) */}
              <div className="-mx-5 mt-2">
                <GiftProgressBar />
                <GiftPicker />
              </div>

              {/* Add another gift CTA — only when at least one gift line exists */}
              {!!cartGift && (
                <div className="mt-5 border border-primary/30 bg-primary/5 p-4 md:mt-6 md:p-5">
                  <p className="mb-3 inline-flex items-center gap-1.5 t-eyebrow uppercase text-primary">
                    <UserPlus className="h-3 w-3" />
                    {t("cart.add_another_gift")}
                  </p>
                  <p className="mb-4 t-body-sm text-cart-muted">
                    {t("cart.add_another_gift_hint")}
                  </p>
                  <Link
                    to="/shop"
                    className="inline-flex items-center gap-2 border border-primary bg-primary px-5 py-2.5 t-eyebrow-lg text-primary-foreground transition-opacity hover:opacity-90"
                  >
                    <ShoppingBag className="h-3.5 w-3.5" />
                    {t("cart.add_another_gift_browse_shop")}
                  </Link>
                </div>
              )}

              {/* ── MOBILE: Summary inline (Ounass pattern) ── */}
              <div className="mt-5 md:hidden">

                {/* Order summary — mobile (accordion: total always visible) */}
                <div className="bg-cart-card">
                  <button
                    type="button"
                    onClick={() => setSummaryOpen((o) => !o)}
                    aria-expanded={summaryOpen}
                    className="flex w-full items-center justify-between gap-3 px-4 py-4 text-start"
                  >
                    <span className="flex items-center gap-2">
                      <span className="t-eyebrow uppercase text-cart-muted">
                        {t("cart.summary_title")}
                      </span>
                      <ChevronDown
                        className={cn(
                          "h-4 w-4 text-cart-muted transition-transform duration-200",
                          summaryOpen && "rotate-180",
                        )}
                      />
                    </span>
                    <span className="text-end leading-tight">
                      <span className="block t-eyebrow uppercase text-cart-muted">
                        {t("cart.subtotal")}
                      </span>
                      <span className="t-h3 t-tabular text-primary">
                        {subtotalLabel}
                      </span>
                    </span>
                  </button>

                  <div className="px-4"><CartFreeShippingNotice /></div>
                  <div
                    className={cn(
                      "grid overflow-hidden transition-all duration-200 ease-out",
                      summaryOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
                    )}
                  >
                    <div className="min-h-0 overflow-hidden">
                      <div className="px-4 pb-5">
                        <dl className="space-y-3 border-t border-border pt-4 t-body-sm">
                          <div className="flex items-center justify-between">
                            <dt className="text-cart-muted">{t("cart.subtotal")}</dt>
                            <dd className="t-tabular text-cart-foreground">
                              {subtotalLabel}
                            </dd>
                          </div>
                          <div className="flex items-center justify-between">
                            <dt className="text-cart-muted">{t(confirmedMoney && shippingQuote ? "cart.quoted_shipping" : "cart.shipping_label")}</dt>
                            <dd className="t-eyebrow uppercase text-primary">
                              {shippingLabel}
                            </dd>
                          </div>
                          <div className="flex flex-col gap-2 border-t border-border pt-3">
                            <div>
                              <p className="t-body text-cart-foreground">
                                {t("cart.subtotal")}
                              </p>
                              <p className="t-micro text-cart-muted">
                                {t("cart.checkout_totals_notice")}
                              </p>
                              <p className="mt-1 max-w-xs t-micro leading-snug text-cart-muted">
                                {t("cart.import_charges_notice")}
                              </p>
                            </div>
                            <p className="t-h3 t-tabular text-primary">
                              {subtotalLabel}
                            </p>
                          </div>
                        </dl>

                        {hasIncompleteGift && (
                          <p className="mt-4 flex items-center justify-center gap-1.5 t-eyebrow uppercase text-destructive">
                            <Gift className="h-3 w-3" />
                            {t("cart.gift_address_required")}
                          </p>
                        )}
                        {!!cartGift && (
                          <p className="mt-2 flex items-start justify-center gap-1.5 text-center t-micro leading-snug text-cart-muted">
                            <CreditCard className="mt-0.5 h-3 w-3 shrink-0 text-primary/60" />
                            <span>{t("gift.payment_card_currency_notice")}</span>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Help row — mobile compact */}
                <div className="border-t border-border bg-cart-card px-4 py-4">
                  <div className="flex items-center justify-between gap-3 t-caption">
                    <span className="t-eyebrow uppercase text-cart-muted">
                      {t("cart.help_title")}
                    </span>
                    <div className="flex items-center gap-4">
                      <a
                        href={`tel:${PHONE_NUMBER}`}
                        className="inline-flex items-center gap-1.5 text-cart-foreground hover:text-primary"
                      >
                        <Phone className="h-3.5 w-3.5 text-primary" />
                        {t("cart.help_call")}
                      </a>
                      <a
                        href={`https://wa.me/${WHATSAPP_NUMBER}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-cart-foreground hover:text-primary"
                      >
                        <svg
                          viewBox="0 0 24 24"
                          className="h-3.5 w-3.5 fill-current text-primary"
                          aria-hidden="true"
                        >
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                        </svg>
                        {t("cart.help_whatsapp")}
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Recommendations — desktop only (مُلغاة في الموبايل) */}
              <div className="mt-8 hidden md:mt-10 md:block">
                <CartRecommendations highlighted={false} />
              </div>
            </div>

            {/* ── RIGHT: Summary (sticky on desktop, hidden on mobile) ── */}
            <aside className="hidden md:sticky md:top-28 md:block md:self-start">
              <div className="bg-cart-card border border-border">
                <div className="px-5 py-5 md:px-6 md:py-6">
                  <h2 className="mb-4 t-h3">
                    {t("cart.summary_title")}
                  </h2>

                  {/* Lines */}
                  <CartFreeShippingNotice />
                  <dl className="space-y-2.5 t-body-sm">
                    <div className="flex items-center justify-between">
                      <dt className="text-cart-muted">{t("cart.subtotal")}</dt>
                      <dd className="t-tabular text-cart-foreground">
                        {subtotalLabel}
                      </dd>
                    </div>
                    <div className="flex items-center justify-between">
                      <dt className="text-cart-muted">{t(confirmedMoney && shippingQuote ? "cart.quoted_shipping" : "cart.shipping_label")}</dt>
                      <dd className="t-eyebrow uppercase text-primary">
                        {shippingLabel}
                      </dd>
                    </div>
                  </dl>

                  <div className="my-4 h-px bg-border" />

                  {/* Grand total */}
                  <div className="flex flex-col gap-2">
                    <div>
                      <p className="t-h3 text-cart-foreground">
                        {t("cart.subtotal")}
                      </p>
                      <p className="t-eyebrow uppercase text-cart-muted">
                        {t("cart.checkout_totals_notice")}
                      </p>
                      <p className="mt-1 max-w-xs t-micro leading-snug text-cart-muted">
                        {t("cart.import_charges_notice")}
                      </p>
                    </div>
                    <p className="t-h2 t-tabular text-primary">
                      {subtotalLabel}
                    </p>
                  </div>

                  {/* Status hint */}
                  {hasIncompleteGift ? (
                    <p className="mt-4 flex items-center justify-center gap-1.5 t-eyebrow uppercase text-destructive">
                      <Gift className="h-3 w-3" />
                      {t("cart.gift_address_required")}
                    </p>
                  ) : (
                    <p className="mt-4 flex items-center justify-center gap-1.5 t-eyebrow uppercase text-cart-muted">
                      <MapPin className="h-3 w-3 text-primary/70" />
                      {t("cart.checkout_next_step")}
                    </p>
                  )}

                  {/* Gift currency notice */}
                  {!!cartGift && (
                    <p className="mt-2 flex items-start justify-center gap-1.5 text-center t-micro leading-snug text-cart-muted">
                      <CreditCard className="mt-0.5 h-3 w-3 shrink-0 text-primary/60" />
                      <span>{t("gift.payment_card_currency_notice")}</span>
                    </p>
                  )}

                  {/* Checkout button (desktop only — mobile uses sticky bar) */}
                  <Button
                    onClick={onCheckout}
                    disabled={
                      isLoading || isSyncing || items.length === 0 || hasIncompleteGift
                    }
                    className="mt-5 hidden h-12 w-full rounded-none bg-primary t-eyebrow uppercase text-primary-foreground hover:bg-primary md:inline-flex"
                  >
                    {isLoading || isSyncing ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      t("cart.checkout")
                    )}
                  </Button>

                  <p className="mt-3 hidden text-center t-micro uppercase tracking-[var(--tracking-wide)] text-cart-muted md:block">
                    {t(productDeliveryPolicy(cartCountry ?? "").installments ? "cart.payment_options_domestic" : "cart.payment_options_checkout")}
                  </p>
                </div>

              </div>

              {/* Help card */}
              <div className="mt-4 border border-border bg-cart-card px-5 py-4">
                <p className="mb-2 t-eyebrow uppercase text-cart-muted">
                  {t("cart.help_title")}
                </p>
                <div className="flex items-center gap-4 t-caption">
                  <a
                    href={`tel:${PHONE_NUMBER}`}
                    className="inline-flex items-center gap-1.5 text-cart-foreground transition-colors hover:text-primary"
                  >
                    <Phone className="h-3.5 w-3.5 text-primary" />
                    {t("cart.help_call")}
                  </a>
                  <a
                    href={`https://wa.me/${WHATSAPP_NUMBER}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-cart-foreground transition-colors hover:text-primary"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      className="h-3.5 w-3.5 fill-current text-primary"
                      aria-hidden="true"
                    >
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                    </svg>
                    {t("cart.help_whatsapp")}
                  </a>
                </div>
              </div>
            </aside>
          </div>
        </div>

        {/* ── Mobile sticky checkout bar ── */}
        <div
          className="fixed inset-x-0 z-30 border-t border-border bg-cart-card px-4 pb-3 pt-3 md:hidden"
          style={{
            bottom:
              "calc(env(safe-area-inset-bottom, 0px) + var(--mobile-nav-height, 0rem))",
          }}
        >
          {/* سطر متابعة التسوّق — فوق شريط الـ Checkout مع مسافة واضحة */}
          <div className="mx-auto mb-3 flex max-w-xl justify-center border-b border-border/40 pb-3">
            <Link
              to="/shop"
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-cart-muted transition-colors hover:text-primary"
            >
              {dir === "rtl" ? (
                <ArrowRight className="h-3.5 w-3.5" />
              ) : (
                <ArrowLeft className="h-3.5 w-3.5" />
              )}
              {t("cart.continue_shopping")}
            </Link>
          </div>
          <div className="mx-auto flex max-w-xl items-center justify-between gap-3">
            <div className="leading-tight">
              <p className="t-eyebrow uppercase text-cart-muted">
                {t("cart.subtotal")}
              </p>
              <p className="t-h3 t-tabular text-primary">
                {subtotalLabel}
              </p>
            </div>
            <Button
              onClick={onCheckout}
              disabled={
                isLoading || isSyncing || items.length === 0 || hasIncompleteGift
              }
              className="h-11 flex-1 rounded-none bg-primary t-eyebrow uppercase text-primary-foreground shadow-sm hover:bg-primary"
            >
              {isLoading || isSyncing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t("cart.checkout")
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Edit gift modal */}
      {giftFlowOpen && (
        <GiftFlow open onClose={() => setGiftFlowOpen(false)} />
      )}

      {/* نافذة معاينة صور المنتج (popup مربّع نصف الشاشة) */}
      {popupImages && (
        <CartImagePopup
          images={popupImages}
          index={popupIndex}
          caption={popupTitle}
          onIndexChange={setPopupIndex}
          onClose={() => setPopupImages(null)}
        />
      )}
    </SiteShell>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CartLine — single product row (image + title + qty + price + gift summary)
// ─────────────────────────────────────────────────────────────────────────────
function CartLine({
  item,
  locale,
  t,
  formatPrice,
  onUpdateQty,
  onRemove,
  onImageClick,
}: {
  item: CartItem;
  locale: "ar" | "en" | "fr";
  t: (key: string, vars?: Record<string, string>) => string;
  formatPrice: (amount: string, currency: string) => string;
  onUpdateQty: (q: number) => Promise<void> | void;
  onRemove: () => Promise<void> | void;
  onImageClick?: () => void;
}) {
  const img = item.product.node.images.edges[0]?.node;
  const { add: addToWishlist, remove: removeFromWishlist, has: hasInWishlist } =
    useWishlist();
  const [moving, setMoving] = React.useState(false);
  const alreadySaved = hasInWishlist(item.product.node.id);
  const navigate = useNavigate();

  // قلب المفضّلة فقط — لا يحذف السطر من السلّة (سلوك متوقَّع: المنتج يبقى ويُحفظ للاحقاً)
  const handleToggleWishlist = async () => {
    if (moving) return;
    setMoving(true);
    try {
      if (alreadySaved) {
        await removeFromWishlist(item.product.node.id);
        toast.success(t("cart.wishlist_removed"));
      } else {
        await addToWishlist(item.product);
        toast.success(t("cart.wishlist_added"), {
          action: {
            label: t("cart.view_wishlist"),
            onClick: () => navigate({ to: "/account/wishlist" }),
          },
          duration: 4000,
        });
      }
    } catch {
      toast.error(t("cart.move_failed"));
    } finally {
      setMoving(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-4 md:gap-5">
        {item.isSampleGift ? (
          // عيّنة هديّة — لا صفحة منتج لها (handle قد يكون فارغاً)، نعرض الصورة فقط بدون رابط
          <div className="relative aspect-square w-28 shrink-0 md:w-32">
            <button
              type="button"
              disabled={!img || !onImageClick}
              onClick={() => img && onImageClick?.()}
              aria-label={t("product.zoom_image")}
              className="relative h-full w-full overflow-hidden bg-cart-page disabled:cursor-default"
            >
              {img && (
                <img
                  src={shopifyImg(img.url, 288, 288)}
                  srcSet={shopifySrcSet(img.url, [256, 360, 480], 1)}
                  sizes="(min-width: 768px) 128px, 112px"
                  alt={img.altText ?? item.product.node.title}
                  className="h-full w-full object-contain"
                  loading="lazy"
                  decoding="async"
                  width={128}
                  height={128}
                />
              )}
            </button>
            {img && onImageClick && (
              <span className="pointer-events-none absolute start-1.5 top-1.5 inline-flex h-7 w-7 items-center justify-center bg-cart-card/90 text-cart-muted backdrop-blur-sm">
                <ZoomIn className="h-3.5 w-3.5" strokeWidth={1.5} />
              </span>
            )}
          </div>
        ) : (
          <div className="relative aspect-square w-28 shrink-0 md:w-32">
            <button
              type="button"
              disabled={!img || !onImageClick}
              onClick={() => img && onImageClick?.()}
              aria-label={t("product.zoom_image")}
              className="relative h-full w-full overflow-hidden bg-cart-page disabled:cursor-default"
            >
              {img && (
                <img
                  src={shopifyImg(img.url, 288, 288)}
                  srcSet={shopifySrcSet(img.url, [256, 360, 480], 1)}
                  sizes="(min-width: 768px) 128px, 112px"
                  alt={img.altText ?? item.product.node.title}
                  className="h-full w-full object-contain"
                  loading="lazy"
                  decoding="async"
                  width={128}
                  height={128}
                />
              )}
            </button>
            {img && onImageClick && (
              <span className="pointer-events-none absolute start-1.5 top-1.5 inline-flex h-7 w-7 items-center justify-center bg-cart-card/90 text-cart-muted backdrop-blur-sm">
                <ZoomIn className="h-3.5 w-3.5" strokeWidth={1.5} />
              </span>
            )}

            {/* زرّ القلب overlay فوق الصورة — يحوّل العنصر للمفضّلة (يُبقي السلّة حيّة بدل الحذف). */}
            <button
              type="button"
              onClick={() => void handleToggleWishlist()}
              disabled={moving}
              aria-pressed={alreadySaved}
              aria-label={alreadySaved ? t("cart.wishlist_saved") : t("cart.wishlist_add")}
              className="absolute end-1.5 top-1.5 inline-flex h-7 w-7 items-center justify-center rounded-none bg-cart-card/90 backdrop-blur-sm shadow-sm transition-colors hover:bg-cart-card disabled:opacity-50"
            >
              {moving ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin text-cart-muted" />
              ) : (
                <Heart
                  className={cn(
                    "h-3.5 w-3.5 text-primary",
                    alreadySaved && "fill-current",
                  )}
                />
              )}
            </button>
          </div>
        )}

        <div className="flex flex-1 flex-col">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              {item.isSampleGift ? (
                <p className="inline-flex max-w-full items-center gap-1 rounded-none bg-primary/10 px-2 py-0.5 t-eyebrow uppercase tracking-[var(--tracking-wide)] text-primary">
                  <Sparkles className="h-3 w-3 shrink-0" />
                  <span className="truncate">{t("gift_sample.line_badge")}</span>
                </p>
              ) : (
                item.product.node.vendor && (
                  <p className="t-eyebrow uppercase text-cart-muted">
                    {item.product.node.vendor}
                  </p>
                )
              )}
              {(() => {
                // دالة موحَّدة: structured DB → variant options → parse للعنوان.
                const presentation = getCartItemPresentation({
                  rawTitle: item.product.node.title,
                  displayName: item.isSampleGift ? item.sampleDisplayName : null,
                  selectedOptions: item.selectedOptions,
                  structuredConcentration: item.isSampleGift ? item.sampleConcentration : null,
                  structuredSizeMl: item.isSampleGift ? item.sampleSizeMl : null,
                });

                return (
                  <>
                    {item.isSampleGift ? (
                      <p className="mt-0.5 block t-body-lg font-serif leading-tight text-cart-foreground">
                        {presentation.title}
                      </p>
                    ) : (
                      <Link
                        to="/product/$handle"
                        params={{ handle: item.product.node.handle }}
                        className="mt-0.5 block t-body-lg font-serif leading-tight text-cart-foreground transition-colors hover:text-primary"
                      >
                        {presentation.title}
                      </Link>
                    )}
                    {presentation.concentrations.length > 0 && (
                      <p className="mt-0.5 t-micro uppercase tracking-[var(--tracking-wide)] text-cart-muted">
                        {presentation.concentrations.join(" · ")}
                      </p>
                    )}
                    {presentation.sizes.length > 0 && (
                      <p className="mt-0.5 text-[11px] text-cart-muted/80">
                        {presentation.sizes.join(" · ")}
                      </p>
                    )}
                  </>
                );
              })()}
              {item.isSampleGift && (
                <>
                  {/* رسالة «مبروك — هذه هديّتك» مع السبب الواضح (يبني الثقة ويُجنّب «أنا ما طلبت»). */}
                  <GiftReason />
                  <p className="mt-1 t-micro leading-snug text-cart-muted">
                    {t("gift_sample.line_keep_or_remove")}
                  </p>
                </>
              )}
            </div>
            {/* السعر — للعيّنة بادج «مجّاناً»، وللعاديّ سعر مشطوب اختياريّ فوق + سعر حالي تحت
             *  بنمط Sephora. حالياً compareAtPrice غير موجود في نموذج السلّة، فيظهر السعر فقط
             *  بلون primary. حين يُضاف الحقل لاحقاً يظهر السعر المشطوب تلقائياً بأحمر بارز. */}
            {item.isSampleGift ? (
              <span className="shrink-0 inline-flex items-center gap-1 rounded-none border border-primary/40 bg-primary/10 px-2.5 py-1 t-eyebrow uppercase tracking-[var(--tracking-wider)] text-primary">
                <Gift className="h-3 w-3" />
                {t("gift_sample.line_price_free")}
              </span>
            ) : (() => {
              // قراءة compareAtPrice من variant المختار إن وُجد (للسعر المشطوب)
              const variant = item.product.node.variants?.edges?.find(
                (e) => e.node.id === item.variantId,
              )?.node as { compareAtPrice?: { amount: string; currencyCode: string } | null } | undefined;
              const compareAt = variant?.compareAtPrice;
              const currentTotal = multiplyContextMoney(item.price, item.quantity);
              const compareTotal = compareAt ? multiplyContextMoney(compareAt, item.quantity) : null;
              const hasDiscount = currentTotal && compareTotal && compareAt &&
                compareAt.currencyCode === item.price.currencyCode && Number(compareAt.amount) > Number(item.price.amount);
              return (
                <div className="shrink-0 flex flex-col items-end gap-0.5">
                  {hasDiscount && (
                    <p className="t-caption text-cart-muted line-through t-tabular">
                      {compareTotal ? formatContextMoney(compareTotal, locale) : null}
                    </p>
                  )}
                  <p className={cn(
                    "t-h3 t-tabular",
                    hasDiscount ? "text-destructive" : "text-primary",
                  )}>
                    {currentTotal ? formatContextMoney(currentTotal, locale) : t("cart.money_pending")}
                  </p>
                </div>
              );
            })()}
          </div>

          {!item.isSampleGift && (
            <div className="mt-auto flex flex-wrap items-center justify-between gap-3 pt-4">
              {/* Quantity selector */}
              <div className="flex items-center border border-border bg-cart-page">
                <button
                  onClick={() => onUpdateQty(item.quantity - 1)}
                  className="px-2.5 py-1.5 text-cart-foreground transition-colors hover:bg-cart-card"
                  aria-label="Decrease"
                >
                  <Minus className="h-3.5 w-3.5" />
                </button>
                <span className="w-9 text-center t-body-sm t-tabular">{item.quantity}</span>
                <button
                  onClick={() => onUpdateQty(item.quantity + 1)}
                  className="px-2.5 py-1.5 text-cart-foreground transition-colors hover:bg-cart-card"
                  aria-label="Increase"
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>

              {/* Inline action: remove */}
              <div className="flex items-center gap-3 t-eyebrow uppercase tracking-[var(--tracking-wide)]">
                <button
                  type="button"
                  onClick={() => void onRemove()}
                  className="inline-flex items-center gap-1.5 text-cart-muted transition-colors hover:text-destructive"
                >
                  <Trash2 className="h-3 w-3 shrink-0" />
                  <span className="leading-tight">{t("cart.remove")}</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* صفّ إجراء عيّنة الهديّة فقط — تُحذف بزرّ مستقلّ */}
      {item.isSampleGift && (
        <div className="flex flex-col items-stretch border-t border-primary/15">
          <button
            type="button"
            onClick={() => void onRemove()}
            className="flex w-full items-center justify-center gap-1.5 py-3 t-eyebrow uppercase tracking-[var(--tracking-wide)] text-cart-muted transition-colors hover:text-destructive"
          >
            <Trash2 className="h-3 w-3" />
            {t("gift_sample.remove")}
          </button>
        </div>
      )}

    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CartLineGiftCard — gift summary (read-only) inside a cart line
// ─────────────────────────────────────────────────────────────────────────────
function CartLineGiftCard({
  gift,
  locale,
  t,
  onEdit,
  onRemove,
}: {
  gift: GiftDetails;
  locale: "ar" | "en" | "fr";
  t: (key: string, vars?: Record<string, string>) => string;
  onEdit: () => void;
  onRemove: () => Promise<void> | void;
}) {
  const a = gift.recipientAddress;
  const isComplete = isGiftContactComplete(gift);

  const countryLabel = (() => {
    if (!a?.country) return null;
    const market = Object.values(MARKETS).find((m) => m.countryCode === a.country);
    if (!market) return a.country;
    return `${market.flag} ${market.labels[locale]}`;
  })();

  return (
    <div className="border border-primary/30 bg-primary/5 p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 t-eyebrow uppercase text-primary">
          <Gift className="h-3.5 w-3.5" />
          {t("cart.gift_note")}
        </span>
        {isComplete ? (
          <span className="inline-flex items-center gap-1 t-eyebrow uppercase text-primary/80">
            <Check className="h-3 w-3" />
            {t("cart.gift_save_hint")}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 t-eyebrow uppercase text-destructive">
            {t("cart.gift_address_required")}
          </span>
        )}
      </div>

      {!isComplete ? (
        <button
          type="button"
          onClick={onEdit}
          className="mb-3 block w-full border border-dashed border-primary/40 bg-cart-page px-3 py-3 text-center t-eyebrow-lg text-primary transition-colors hover:bg-primary/10"
        >
          + {t("gift.title")}
        </button>
      ) : (
        <div className="mb-3 space-y-1.5 t-body-sm leading-snug text-cart-foreground/90">
          {gift.recipientName?.trim() && (
            <SummaryRow
              label={t("cart.gift_recipient_name_label")}
              value={gift.recipientName.trim()}
            />
          )}
          {gift.recipientPhone && (
            <SummaryRow
              label={t("cart.gift_recipient_phone_label")}
              value={gift.recipientPhone}
              dir="ltr"
            />
          )}
          {gift.message && (
            <SummaryRow
              label={t("cart.gift_message_label")}
              value={`"${gift.message}"`}
              italic
            />
          )}
          <SummaryRow
            label={t("cart.gift_section_address")}
            value={[countryLabel, a?.city, a?.address1, a?.zip]
              .filter(Boolean)
              .join(" · ")}
          />
        </div>
      )}

      <div className="mt-3 flex items-center justify-between gap-2 border-t border-primary/20 pt-3">
        <button
          type="button"
          onClick={() => void onRemove()}
          className="inline-flex items-center gap-1 t-eyebrow uppercase text-cart-muted transition-colors hover:text-destructive"
        >
          <X className="h-3 w-3" />
          {t("cart.gift_remove")}
        </button>
        <button
          type="button"
          onClick={onEdit}
          className="inline-flex items-center gap-1.5 border border-primary px-3 py-1.5 t-eyebrow uppercase text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
        >
          <Pencil className="h-3 w-3" />
          {t("cart.gift_edit")}
        </button>
      </div>
    </div>
  );
}

function SummaryRow({
  label,
  value,
  italic,
  dir,
}: {
  label: string;
  value: string;
  italic?: boolean;
  dir?: "ltr" | "rtl";
}) {
  return (
    <div className="grid grid-cols-[110px_1fr] gap-2">
      <span className="truncate t-eyebrow uppercase text-cart-muted">
        {label}
      </span>
      <span className={italic ? "italic" : ""} dir={dir}>
        {value}
      </span>
    </div>
  );
}
